import { useQueries } from "react-query";
import { randomImageProvider } from "./RandomImageProvider";

export function htmlDecode(input) {
  var doc = new DOMParser().parseFromString(input, "text/html");
  return doc.documentElement.textContent;
}

export const localizeDate = (date, locale) => {
  return new Date(date).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export async function getBlogById(contentId) {
  const url = `/rest/api/search?cql=type=blogpost and id=${contentId}&limit=1&expand=content.history,content.space,content.metadata.labels,content.metadata.likes,content.children.comment`;
  try {
    return window.AP.request(url);
  } catch (e) {
    console.log("cant fetch blogpost", contentId);
    console.log(e);
    return undefined;
  }
}

const coverPictureCache = {};

function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
export async function getCoverPictureOfContent(contentId, isPage) {
  if (contentId in coverPictureCache) {
    if (coverPictureCache[contentId] === "error") {
      return randomImageProvider.getRandomImage(contentId);
    }
    return coverPictureCache[contentId];
  }
  try {
    const KEY = "cover-picture-id-published";
    const url = `/api/v2/${
      isPage ? "pages" : "blogposts"
    }/${contentId}/properties`;
    const response = await window.AP.request(url);
    const data = JSON.parse(response.body);

    try {
      const body = data?.results?.find((property) => property.key === KEY);
      const isBodyValueJSON = isJsonString(body?.value);
      const fileIdOrDirectLink = isBodyValueJSON
        ? JSON.parse(body?.value)?.id
        : body?.value || "";
      if (isUUID(fileIdOrDirectLink)) {
        const attachmentLink = await findFileByIdInAttachments(
          contentId,
          fileIdOrDirectLink,
          isPage
        );
        coverPictureCache[contentId] = attachmentLink;
        return attachmentLink;
      }
      if (fileIdOrDirectLink) {
        coverPictureCache[contentId] = fileIdOrDirectLink;
        return fileIdOrDirectLink;
      }

      return randomImageProvider.getRandomImage(contentId);
    } catch (e) {
      coverPictureCache[contentId] = "error";
      return randomImageProvider.getRandomImage(contentId);
    }
  } catch (e) {
    coverPictureCache[contentId] = "error";
    return randomImageProvider.getRandomImage(contentId);
  }
}

export async function getIconOfContent(contentId) {
  const url = `/api/v2/pages/${contentId}/properties/emoji-title-published`;
  try {
    const response = await window.AP.request(url);
    const body = JSON.parse(response.body);
    return body.value;
  } catch (e) {
    return;
  }
}

async function findFileByIdInAttachments(contentId, fileId, isPage) {
  const url = `/api/v2/${
    isPage ? "pages" : "blogposts"
  }/${contentId}/attachments`;
  let response = await window.AP.request(url);
  let results = JSON.parse(response.body).results;
  let links = JSON.parse(response.body)._links;
  try {
    do {
      let found = results.find((r) => r.fileId === fileId);
      if (found) {
        return `${window.AP._hostOrigin}/wiki${found.downloadLink}`;
      }
      if ("next" in links) {
        response = JSON.parse(await window.AP.request(links.next));
        results = JSON.parse(response.body).results;
        links = JSON.parse(response.body)._links;
      }
    } while ("next" in links);
  } catch (e) {
    return null;
  }
}

function isUUID(uuid) {
  return (
    uuid.match(
      "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
    ) !== null
  );
}

const DEFAULT_COVER_NEWS_IMAGE =
  "https://images.unsplash.com/photo-1526470608268-f674ce90ebd4?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2048&q=80";

export const mapBlogpost = (blogpost, isCosmosInstalled, locale) => ({
  id: blogpost.content.id,
  contentId: blogpost.content.id,
  type: blogpost.content.type,
  name: blogpost.content.title,
  numberOfComments: blogpost.content.children.comment.results.length,
  numberOfLikes: blogpost?.content?.metadata?.likes?.count,
  space: { name: blogpost.content.space.name },
  excerpt: htmlDecode(blogpost.excerpt),
  labels: blogpost.content.metadata?.labels?.results.map((l) => l.label) ?? [],
  url: `${window.AP._hostOrigin}/wiki${blogpost.url}`,
  coverPictureCallback: async () =>
    getCoverPictureOfContent(blogpost.content.id, false),
  createdBy: blogpost.content.history.createdBy.displayName,
  createdByProfileLink: `${window.AP._hostOrigin}/wiki/people/${blogpost.content.history.createdBy.accountId}`,
  avatarUrl: `${window.AP._hostOrigin}${blogpost.content.history.createdBy.profilePicture.path}`,
  lastModified: localizeDate(blogpost.lastModified, locale),
  created: localizeDate(
    blogpost.content.history.createdDate,
    locale || "en-US"
  ),
});

export const useBlogs = (data, isCosmosInstalled, locale) =>
  useQueries(
    data.map((blogpost) => {
      return {
        queryKey: ["blog", blogpost.contentId, blogpost.id],
        queryFn: () => {
          if (blogpost.contentId === 0) {
            return { isNew: true, loading: false, id: blogpost.id };
          }
          return getBlogById(blogpost.contentId);
        },
        retry: 0,
        placeholderData: {
          contentId: blogpost.contentId,
          loading: true,
          isPlaceholder: true,
        },
        select: (response) => {
          if (response.isPlaceholder || response.isNew) {
            return response;
          }
          const body = JSON.parse(response.body);

          if (!body?.results?.length) return {};

          const result = body.results.map((p) => {
            return {
              ...mapBlogpost(p, isCosmosInstalled, locale),
              id: data.find((x) => x.id.toString() === blogpost.id).id,
            };
          });
          return result[0];
        },
      };
    })
  );
